import { ProjectDto, ProjectStatusDto, StatusDto } from '@/api/ApiClient';

/**
 * UI gate for showing **Delete** on project details. Mirrors the project handler’s approach in spirit:
 * - **Submitted** workflow (`StatusDto.Submitted`): delete only remains plausible when lifecycle is still
 *   **Pending** (`ProjectAuthHandler` exception). We do **not** describe this as “published workflow”
 *   (`StatusDto.Published`)—that is not the product wording for this rule.
 * - **Marked public** (`isPublished`): treat as non-deletable in the UI regardless of lifecycle.
 *
 * Other checks (privileges, accessibility, technique match, etc.) remain server-side only.
 */
export type ProjectDeleteUiGate = Pick<ProjectDto, 'status' | 'projectStatus' | 'isPublished'>;

export function isProjectDeleteAllowedByUiGate(project: ProjectDeleteUiGate): boolean {
  if (project.isPublished === true) {
    return false;
  }
  if (project.status !== StatusDto.Submitted) {
    return true;
  }
  return project.projectStatus === ProjectStatusDto.Pending;
}

/** Non-null when Delete should be disabled based on these fields alone (UI hint). */
export function getProjectDeleteDisabledReason(project: ProjectDeleteUiGate): string | null {
  if (project.isPublished === true) {
    return 'Delete is not available while the project is marked public.';
  }
  if (project.status === StatusDto.Submitted && project.projectStatus !== ProjectStatusDto.Pending) {
    return 'Delete is blocked for this submitted version unless project lifecycle status is still Pending (other authorization rules may still apply).';
  }
  return null;
}
